import Link from "next/link";
import { ArrowRight, Camera } from "lucide-react";
import { useFetchAttractions, useFetchAttractionsThemes } from "../places/attractions/_hook";
import { Attraction } from "@/types/places";
import { shortenAddress } from "@/utils/util";
import { SupportedLanguage } from "@/config/store/globalStore";
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslation } from "@/hooks/useTranslation";
import Image from "next/image";
import { BLUR_URL, getUnifiedAttractionsImages } from "@/utils/images";

export default function FeaturedAttractions() {
  const { data: attractions, loading: attractionsLoading } = useFetchAttractions('');
  const { data: attractionsThemes, loading: attractionsThemesLoading } = useFetchAttractionsThemes();
  const { language } = useLanguage();
  const { t } = useTranslation();

  // ID가 1인 테마 찾기 ("한국에 왔다면 꼭 가봐야하는 곳")
  const mustVisitTheme = attractionsThemes?.result?.find((theme: any) => theme.id === 1);
  const mustVisitAttractions = mustVisitTheme?.value || [];

  // ID가 2인 테마 찾기 ("현지인도 자주가는 곳")
  const localFavoriteTheme = attractionsThemes?.result?.find((theme: any) => theme.id === 2);
  const localFavoriteAttractions = localFavoriteTheme?.value || [];

  // 해당 테마에 속하는 관광지들 필터링
  const filteredAttractions = attractions?.result?.filter((attraction: any) => 
    mustVisitAttractions.includes(attraction.id)
  );

  const filteredLocalAttractions = attractions?.result?.filter((attraction: any) => 
    localFavoriteAttractions.includes(attraction.id)
  );

  return (
    <>
    <section className="mb-20">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">
          {mustVisitTheme?.label?.[language as SupportedLanguage] || t('featured_must_visit_attractions')}
        </h2>
        <Link 
          href={`/${language}/places/attractions`}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
        >
          {t('view_all')}
          <ArrowRight className="ml-1 w-4 h-4" />
        </Link>
      </div>
      
      {attractionsLoading || attractionsThemesLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6 gap-y-12">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-48 sm:h-52 md:h-48 lg:h-48 bg-gray-200 rounded-xl mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6 gap-y-12">
          {filteredAttractions
            ?.sort(() => Math.random() - 0.5)
            ?.slice(0, 8)
            ?.map((attraction: Attraction) => (
            <Link 
              key={attraction.id}
              href={`/${language}/places/attractions/${attraction.id}`}
            >
              <div className="group cursor-pointer">
                <div className="mb-4 overflow-hidden rounded-xl relative h-48 sm:h-52 md:h-48 lg:h-48">
                  <Image
                    src={getUnifiedAttractionsImages(attraction.id, attraction.images)[0]}
                    alt={`${attraction.name[language as SupportedLanguage]}-image`}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    placeholder="blur"
                    blurDataURL={BLUR_URL}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-gray-600 transition-colors break-words">
                    {attraction.name[language as SupportedLanguage]}
                  </h3>
                </div>
                
                <div className="flex flex-col flex-wrap text-sm text-gray-600 mt-1">
                  <div>
                    {shortenAddress(attraction.address?.[language as SupportedLanguage], language as SupportedLanguage)}
                  </div>
                  <div className="flex flex-wrap items-center text-sm text-gray-600 mt-1.5">
                    <span key={attraction.category[language as SupportedLanguage]}>
                      {attraction.category[language as SupportedLanguage]}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
    <section className="mb-20">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">
          {localFavoriteTheme?.label?.[language as SupportedLanguage] || t('featured_local_attractions')}
        </h2>
        <Link 
          href={`/${language}/places/attractions`}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
        >
          {t('view_all')}
          <ArrowRight className="ml-1 w-4 h-4" />
        </Link>
      </div>
      
      {attractionsLoading || attractionsThemesLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6 gap-y-12">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-48 sm:h-52 md:h-48 lg:h-48 bg-gray-200 rounded-xl mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6 gap-y-12">
          {filteredLocalAttractions
            ?.sort(() => Math.random() - 0.5)
            ?.slice(0, 8)
            ?.map((attraction: Attraction) => (
            <Link 
              key={attraction.id}
              href={`/${language}/places/attractions/${attraction.id}`}
            >
              <div className="group cursor-pointer">
                <div className="mb-4 overflow-hidden rounded-xl relative h-48 sm:h-52 md:h-48 lg:h-48">
                  <Image 
                    src={getUnifiedAttractionsImages(attraction.id, attraction.images)[0]}
                      alt={`${attraction.name[language as SupportedLanguage]}-image`}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-300"
                      placeholder="blur"
                      blurDataURL={BLUR_URL}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-gray-600 transition-colors break-words">
                    {attraction.name[language as SupportedLanguage]}
                  </h3>
                </div>
                
                <div className="flex flex-col flex-wrap text-sm text-gray-600 mt-1">
                  <div>
                    {shortenAddress(attraction.address?.[language as SupportedLanguage], language as SupportedLanguage)}
                  </div>
                  <div className="flex flex-wrap items-center text-sm text-gray-600 mt-1.5">
                    <span key={attraction.category[language as SupportedLanguage]}>
                      {attraction.category[language as SupportedLanguage]}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
    </>
  );
} 